import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { countriesList, countryCodeToFlagEmoji, getCountryNameByCode } from "../Account/Countries";

type Props = {
  id: string;
  value: string;
  onChange: (code: string) => void;
};

type Row =
  | { kind: "clear" }
  | { kind: "country"; code: string; country: string };

export function ProfileCountryCombobox({ id, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const filteredCountries = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countriesList;
    return countriesList.filter(
      (c) =>
        c.country.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [query]);

  const rows = useMemo((): Row[] => {
    return [
      { kind: "clear" as const },
      ...filteredCountries.map((c) => ({ kind: "country" as const, ...c })),
    ];
  }, [filteredCountries]);

  const noCountryMatches =
    query.trim() !== "" && filteredCountries.length === 0;

  const updatePanelPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPanelStyle({
      position: "fixed",
      left: rect.left,
      top: rect.bottom + 2,
      width: Math.max(rect.width, 280),
      zIndex: 10050,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePanelPosition();
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return;
    const onWin = () => updatePanelPosition();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
      setQuery("");
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlighted(0);
      requestAnimationFrame(() => {
        searchRef.current?.focus({ preventScroll: true });
      });
    }
  }, [open]);

  useEffect(() => {
    setHighlighted((h) => Math.min(h, Math.max(0, rows.length - 1)));
  }, [rows.length]);

  /** Keep highlighted row visible inside the listbox only — avoid `scrollIntoView`, which scrolls the page. */
  useLayoutEffect(() => {
    if (!open) return;
    const listbox = listboxRef.current;
    const row = rowRefs.current[highlighted];
    if (!listbox || !row) return;
    const lr = listbox.getBoundingClientRect();
    const rr = row.getBoundingClientRect();
    if (rr.top < lr.top) {
      listbox.scrollTop -= lr.top - rr.top;
    } else if (rr.bottom > lr.bottom) {
      listbox.scrollTop += rr.bottom - lr.bottom;
    }
  }, [highlighted, open]);

  const selectedLabel = value ? getCountryNameByCode(value) : "";
  const displayFlag = value ? countryCodeToFlagEmoji(value) : "—";

  const selectRow = useCallback(
    (row: Row) => {
      if (row.kind === "clear") {
        onChange("");
      } else {
        onChange(row.code);
      }
      setOpen(false);
      setQuery("");
      triggerRef.current?.focus();
    },
    [onChange]
  );

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery("");
      triggerRef.current?.focus();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, rows.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const row = rows[highlighted];
      if (row) selectRow(row);
    }
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!open) setOpen(true);
    }
  };

  const listboxId = `${id}-listbox`;
  const searchId = `${id}-search`;

  const panel = open
    ? createPortal(
      <div
        ref={panelRef}
        className="flex max-h-[min(320px,calc(100vh-120px))] flex-col overflow-hidden rounded-none border border-white/20 bg-[#4f2189] shadow-lg"
        style={panelStyle}
      >
        <input
          ref={searchRef}
          id={searchId}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlighted(0);
          }}
          onKeyDown={onSearchKeyDown}
          placeholder="Search country…"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Search countries"
          className="w-full shrink-0 border-b border-white/15 bg-[#381663] px-2 py-2 font-chakra text-[12px] font-normal leading-[16px] tracking-normal text-white placeholder:text-white/40 outline-none [leading-trim:none] focus:border-b focus:border-white/25"
        />
        <div
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          aria-label="Countries"
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        >
          {noCountryMatches ? (
            <p className="px-2 py-2 text-center font-chakra text-[11px] font-normal leading-snug text-white/55 [leading-trim:none]">
              No countries match
            </p>
          ) : null}
          {rows.map((row, i) => {
            const active = i === highlighted;
            const isClear = row.kind === "clear";
            const key = isClear ? "__clear" : `${row.code}-${row.country}`;
            const flag = isClear ? "—" : countryCodeToFlagEmoji(row.code);
            const name = isClear ? "No country" : row.country;
            const isSelected = isClear ? value === "" : value === row.code;

            return (
              <button
                key={key}
                type="button"
                role="option"
                aria-selected={isSelected}
                ref={(el) => {
                  rowRefs.current[i] = el;
                }}
                onMouseEnter={() => setHighlighted(i)}
                onClick={() => selectRow(row)}
                className={`flex w-full min-w-0 items-center gap-2 px-2 py-2 text-left font-chakra text-[12px] font-normal leading-[16px] tracking-normal [leading-trim:none] ${
                  active ? "bg-white/15 text-white" : "text-white/95 hover:bg-white/10"
                } ${isSelected ? "ring-1 ring-inset ring-[#D2FF00]/40" : ""}`}
              >
                <span className="shrink-0 text-[18px] leading-none" aria-hidden>
                  {flag}
                </span>
                <span className="min-w-0 flex-1 truncate">{name}</span>
              </button>
            );
          })}
        </div>
      </div>,
      document.body
    )
    : null;

  return (
    <div className="relative h-full min-h-0 min-w-0 flex-1">
      <button
        ref={triggerRef}
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={selectedLabel ? `Country: ${selectedLabel}` : "Country"}
        title={selectedLabel || undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
        className="h-full w-full min-h-0 min-w-0 cursor-pointer border-0 bg-transparent py-0 pr-7 text-left text-[20px] leading-none text-white outline-none focus-visible:ring-1 focus-visible:ring-white/40"
      >
        <span className="block pl-0.5">{displayFlag}</span>
      </button>
      <span
        className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-sm text-white/70"
        aria-hidden
      >
        ▼
      </span>
      {panel}
    </div>
  );
}
